import Intercom, {
  show,
  hide,
  onHide,
  onShow,
  showSpace,
  showNewMessage,
  shutdown,
  update,
  showConversation,
} from "@intercom/messenger-js-sdk";
import { useAuth } from "@shared/hooks/useAuth";
import { appConfig } from "@shared/utils/appConfig";
import { useIntercomStore } from "@store/intercom.store";
import { useEffect } from "react";

const INTERCOM_APP_ID = appConfig.INTERCOM_APP_ID;

type IntercomSpace = "home" | "messages" | "help" | "news" | "tasks";
interface UserArgs {
  email?: string;
  created_at?: number;
  name?: string;
  user_id?: string;
  [key: string]: any;
}

interface IntercomIntegrationValues {
  isIntercomOpen: boolean;
  showSpace: (spaceName: IntercomSpace) => void;
  showNewMessage: (prePopulatedContent: string) => void;
  hide: () => void;
  show: () => void;
  onShow: (callback: () => void) => void;
  onHide: (callback: () => void) => void;
  shutdown: () => void;
  update: (args?: UserArgs) => void;
  showConversation: (conversationId: string) => void;
}

const useIntercomIntegrationDefault = (): IntercomIntegrationValues => {
  const { isAuthenticated, user } = useAuth();
  const mock = useIntercomIntegrationMock();
  const setIsIntercomOpen = useIntercomStore(
    (state) => state.setIsIntercomOpen
  );
  const isIntercomOpen = useIntercomStore((state) => state.isIntercomOpen);

  useEffect(() => {
    onShow(() => setIsIntercomOpen(true));
    onHide(() => setIsIntercomOpen(false));
  }, [setIsIntercomOpen]);

  if (!user?.id || !isAuthenticated) return mock;

  Intercom({
    app_id: INTERCOM_APP_ID!,
    user_id: user.trackingId,
    user_hash: user.hash,
    name: user.name,
    email: user.email,
    mls_providers: user.allowedMlsProviders.join(", "),
    subscription_plan_id: user.subscriptionPlanId,
    hide_default_launcher: true,
  });

  return {
    isIntercomOpen,
    showSpace,
    showNewMessage,
    hide,
    show,
    onShow,
    onHide,
    shutdown,
    update: (args?: UserArgs) => {
      update({ ...(args || {}) });
    },
    showConversation,
  };
};

const useIntercomIntegrationMock = () => {
  const intercomConsole = (message?: any, ...optionalParams: any[]) => {
    console.debug(`[INTERCOM MOCK]: ${message}`, optionalParams);
  };
  const values: IntercomIntegrationValues = {
    isIntercomOpen: false,
    showSpace: (spaceName) => {
      intercomConsole("showSpace", spaceName);
    },
    showNewMessage: (prePopulatedContent) => {
      intercomConsole("showNewMessage", prePopulatedContent);
    },
    hide: () => {
      intercomConsole("hide");
    },
    show: () => {
      intercomConsole("show");
    },
    onShow: (f) => {
      intercomConsole("onShow", f);
    },
    onHide: (f) => {
      intercomConsole("onHide", f);
    },
    shutdown: () => {
      intercomConsole("shutdown");
    },
    update: (args) => {
      intercomConsole("update", args);
    },
    showConversation: (args) => {
      intercomConsole("showConversation", args);
    },
  };

  return values;
};

export const useIntercomIntegration = INTERCOM_APP_ID
  ? useIntercomIntegrationDefault
  : useIntercomIntegrationMock;
